/**
 * The escape hatch: throw away every service worker and every cache this
 * origin holds, then reload onto whatever the server is serving now.
 *
 * On 2026-08-25 the stable server was briefly sending `/sw.js` with
 * `Cache-Control: public, max-age=31536000, immutable`. Safari honours that on
 * a worker update check, so it stayed pinned to one old worker across every
 * deploy, `/fly` hung on "Starting engine…", and — this is the part that made
 * it an incident rather than a nuisance — the user had no way out. A hard
 * reload reloads the *page*; the worker controlling it is untouched, and it
 * answers the reload's own navigation from its own stale cache. Recovery took
 * a console snippet.
 *
 * `register-sw.ts`'s `updateViaCache: "none"` is the fix for that specific
 * cause. This module is the fix for the *class*: whatever pins a worker next
 * time, the shell can already unpin itself (`shell-runtime.ts` binds the
 * shortcut below). It is deliberately the console snippet from that day,
 * turned into a function tests can hold to.
 *
 * Two things now reach it. `shell-runtime.ts` binds the chord; `register-sw.ts`
 * runs it on any boot whose URL carries `?unstale` (`unstale-flag.ts`), which is
 * the half of that hatch a keyboard cannot reach — an iPad has no
 * `Ctrl`+`Shift`+`Backspace`, and a URL is the one instruction that can be
 * given over the phone. `boot-health.ts` is the third caller, and the only one
 * that needs no user at all.
 *
 * Everything here degrades to a plain reload rather than throwing: in an
 * insecure context `navigator.serviceWorker` and `caches` are both absent, and
 * a page with nothing cached is exactly the page this routine is trying to
 * produce.
 */

/** Shown on the shell's own canvas before the reload — see `render/notice-screen.ts`. */
export const UNSTALE_MESSAGE = "CLEARING OFFLINE CACHE";

/**
 * A floor under how long the notice stays up. The clearing itself can finish
 * in a few milliseconds on a page that has nothing cached, and a message that
 * flashes for one frame before the reload is indistinguishable from a glitch —
 * the user has to be able to tell the shortcut fired.
 */
export const UNSTALE_HOLD_MS = 900;

/**
 * `Ctrl`+`Shift`+`Backspace`, and nothing else, is the shortcut.
 *
 * Backspace because the gesture is "erase what is stored"; two modifiers
 * because a bare key would let a mistyped menu press wipe a cache someone flew
 * offline on. It collides with nothing on either side:
 *
 * - The shell's own keys are `ArrowUp`/`ArrowDown`/`Enter`/`Space`/`Escape`
 *   (`shell-runtime.ts`'s `menuCommandFor`), and its audio-unlock gesture
 *   explicitly excludes any chord holding Ctrl, Meta or Alt (`isActivationKey`)
 *   — so this chord is inert in the shell as it stands today, which
 *   `shell-flow` asserts rather than assumes.
 * - The browsers own `Ctrl`/`Cmd`+`R`, `Ctrl`+`Shift`+`R`, `Ctrl`+`Shift`+`Delete`,
 *   `Ctrl`+`Shift`+`I`/`J`/`K`/`C`, `Ctrl`+`Shift`+`N`/`P`/`T`, `F5` and `F12`.
 *   `Ctrl`+`Shift`+`Backspace` is bound by none of Chrome, Firefox or Safari,
 *   and unlike `Ctrl`+`Shift`+`U` it is not swallowed by IBus's Unicode entry
 *   on Linux desktops.
 *
 * Meta is rejected as well as required-absent so that macOS `Cmd`-chords can
 * never reach here.
 */
export function isUnstaleShortcut(event: KeyboardEvent): boolean {
  return event.ctrlKey && event.shiftKey && !event.altKey && !event.metaKey && event.key === "Backspace";
}

async function unregisterWorkers(): Promise<void> {
  if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) return;
  try {
    const registrations = await navigator.serviceWorker.getRegistrations();
    await Promise.all(registrations.map((registration) => registration.unregister()));
  } catch {
    // A browser that refuses to enumerate registrations (private mode, a
    // policy-blocked origin) still gets the cache sweep and the reload.
  }
}

async function deleteCaches(): Promise<void> {
  const storage = (globalThis as { caches?: CacheStorage }).caches;
  if (!storage) return;
  try {
    const names = await storage.keys();
    // Every cache, not just `red-opus-precache-*`: the point of this routine is
    // to be the thing that works when the assumptions have already failed.
    await Promise.all(names.map((name) => storage.delete(name)));
  } catch {
    // Same bargain as above — a quota or policy error must not cost the reload.
  }
}

function hold(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Unregister, then sweep, then reload. Sequential rather than concurrent so
 * the worker is already gone before its cache disappears underneath it, and so
 * the order is one assertable list rather than a race.
 */
export async function unstale(): Promise<void> {
  await unregisterWorkers();
  await deleteCaches();
  await hold(UNSTALE_HOLD_MS);
  if (typeof window !== "undefined") window.location.reload();
}
